import { useMutation, useQueryClient } from '@tanstack/react-query'
import useAxiosPrivate from '../../api/hooks/useAxiosPrivate'
import useToastNotification from '../useToastNotification'
import { validateSupportImages } from '../../utils/validations/supportValidations'
import { invalidateSupportRequest } from './useFetchSupportRequestById'

// One image per request: the server caps the whole upload body at 6 MB, so a batch
// of 5 MB images would be rejected even though each image is within the limit.
export const uploadSupportRequestImages = async (
  axiosPrivate: ReturnType<typeof useAxiosPrivate>,
  supportRequestId: number,
  images: File[]
) => {
  for (const image of images) {
    const formData = new FormData()
    formData.append('images[]', image, image.name)

    await axiosPrivate.post(
      `/api/support-requests/${supportRequestId}/images`,
      formData
    )
  }
}

type AddSupportRequestImagesParams = {
  supportRequestId: number
  images: File[]
  currentCount: number
}

type AddSupportRequestImagesMutationProps = {
  params: AddSupportRequestImagesParams
}

const useAddSupportRequestImages = () => {
  const axiosPrivate = useAxiosPrivate()
  const queryClient = useQueryClient()
  const { sendToastMessage, showApiErrorMessage } = useToastNotification()

  const { mutate, isPending } = useMutation({
    mutationFn: async ({ params }: AddSupportRequestImagesMutationProps) => {
      return await uploadSupportRequestImages(
        axiosPrivate,
        params.supportRequestId,
        params.images
      )
    },
    onSettled: (_, __, variables) => {
      invalidateSupportRequest(queryClient, variables.params.supportRequestId)
    },
    onSuccess: (_, variables) => {
      sendToastMessage(
        'success',
        variables.params.images.length === 1 ? 'Image added.' : 'Images added.'
      )
    },
    onError: (error) => {
      showApiErrorMessage(
        error,
        'Something went wrong while uploading the images. Please try again.'
      )
    },
  })

  const handleAddSupportRequestImages = (
    params: AddSupportRequestImagesParams
  ) => {
    const validationError = validateSupportImages(
      params.images,
      params.currentCount
    )

    if (validationError !== undefined) {
      sendToastMessage('error', validationError)
      return
    }

    mutate({ params })
  }

  return { isPending, handleAddSupportRequestImages } as const
}

export default useAddSupportRequestImages
